import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCurrentLanguage } from '../config/languageConfig';
import { getSearchRanking } from '../services/SearchService';
import { SearchRankingItem, SearchRankingResponse, RecentSearchItem, SearchType } from '../types/search';

const SearchPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchRanking, setSearchRanking] = useState<SearchRankingItem[]>([]);
  const [lastUpdated, setLastUpdated] = useState<string>('');
  const [recentSearches, setRecentSearches] = useState<RecentSearchItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentFoodIndex, setCurrentFoodIndex] = useState(0);
  const currentLang = getCurrentLanguage();

  // 언어별 음식 이름들
  const getFoodItemsByLanguage = () => {
    const foodItems = {
      'en': ['Chicken', 'Tteokbokki', 'Bibimbap', 'Bulgogi', 'Samgyeopsal', 'Jjajang'],
      'ko': ['치킨', '떡볶이', '비빔밥', '불고기', '삼겹살', '짜장면'],
      'ja': ['チキン', 'トッポッキ', 'ビビンバ', 'プルコギ', 'サムギョプサル', 'ジャージャー麺'],
      'zh-CN': ['炸鸡', '年糕', '拌饭', '烤肉', '五花肉', '炸酱面'],
      'zh-TW': ['炸雞', '年糕', '拌飯', '烤肉', '五花肉', '炸醬麵']
    };
    return foodItems[currentLang] || foodItems['en'];
  };

  // 언어별 텍스트
  const getTexts = () => {
    const texts = {
      'en': {
        searchPlaceholder: 'Search for',
        popularSearches: 'Popular Searches',
        weeklyRanking: 'Weekly Ranking',
        recentSearches: 'Recent Searches',
        clearAll: 'Clear All',
        noRecentSearches: 'No recent searches',
        cancel: 'Cancel',
        lastUpdated: 'Last updated',
        minutesAgo: 'minutes ago',
        hoursAgo: 'hours ago',
        daysAgo: 'days ago',
        justNow: 'Just now'
      },
      'ko': {
        searchPlaceholder: '검색하기',
        popularSearches: '인기 검색어',
        weeklyRanking: '주간 랭킹',
        recentSearches: '최근 검색어',
        clearAll: '전체 삭제',
        noRecentSearches: '최근 검색어가 없습니다',
        cancel: '취소',
        lastUpdated: '마지막 업데이트',
        minutesAgo: '분 전',
        hoursAgo: '시간 전',
        daysAgo: '일 전',
        justNow: '방금 전'
      },
      'ja': {
        searchPlaceholder: '検索する',
        popularSearches: '人気検索語',
        weeklyRanking: '週間ランキング',
        recentSearches: '最近の検索語',
        clearAll: '全て削除',
        noRecentSearches: '最近の検索語がありません',
        cancel: 'キャンセル',
        lastUpdated: '最終更新',
        minutesAgo: '分前',
        hoursAgo: '時間前',
        daysAgo: '日前',
        justNow: 'たった今'
      },
      'zh-CN': {
        searchPlaceholder: '搜索',
        popularSearches: '热门搜索',
        weeklyRanking: '周排行榜',
        recentSearches: '最近搜索',
        clearAll: '全部删除',
        noRecentSearches: '没有最近搜索',
        cancel: '取消',
        lastUpdated: '最后更新',
        minutesAgo: '分钟前',
        hoursAgo: '小时前',
        daysAgo: '天前',
        justNow: '刚刚'
      },
      'zh-TW': {
        searchPlaceholder: '搜尋',
        popularSearches: '熱門搜尋',
        weeklyRanking: '週排行榜',
        recentSearches: '最近搜尋',
        clearAll: '全部刪除',
        noRecentSearches: '沒有最近搜尋',
        cancel: '取消',
        lastUpdated: '最後更新',
        minutesAgo: '分鐘前',
        hoursAgo: '小時前',
        daysAgo: '天前',
        justNow: '剛剛'
      }
    };
    return texts[currentLang] || texts['en'];
  };

  const foodItems = getFoodItemsByLanguage();
  const texts = getTexts();

  // 플레이스홀더 롤링 효과
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentFoodIndex((prevIndex) => (prevIndex + 1) % foodItems.length);
    }, 2000);

    return () => clearInterval(interval);
  }, [foodItems.length]);

  // 검색 랭킹 데이터 로드
  useEffect(() => {
    const loadSearchRanking = async () => {
      setLoading(true);
      try {
        const data = await getSearchRanking();
        
        // 현재 언어에 맞는 랭킹 데이터 선택
        let langKey: keyof SearchRankingResponse;
        switch (currentLang) {
          case 'ja':
            langKey = 'ja';
            break;
          case 'zh-CN':
            langKey = 'zhCn';
            break;
          case 'zh-TW':
            langKey = 'zhTw';
            break;
          default:
            langKey = 'en';
        }
        
        setSearchRanking(data[langKey] || []);
        setLastUpdated(data.lastUpdated || ''); // 실제 lastUpdated 시간 저장
      } catch (error) {
        console.error('Failed to load search ranking:', error);
        setSearchRanking([]);
      } finally {
        setLoading(false);
      }
    };

    loadSearchRanking();
  }, [currentLang]);

  // 최근 검색어 로드
  useEffect(() => {
    const loadRecentSearches = () => {
      try {
        const stored = localStorage.getItem('recentSearches');
        if (stored) {
          const parsed = JSON.parse(stored);
          
          // 배열이 아니거나 빈 배열인 경우 처리
          if (!Array.isArray(parsed) || parsed.length === 0) {
            setRecentSearches([]);
            localStorage.removeItem('recentSearches');
            return;
          }
          
          // 기존 문자열 배열인 경우 객체 배열로 변환 (MENU 타입으로 기본 설정)
          if (typeof parsed[0] === 'string') {
            const converted = parsed.map((keyword: string) => ({
              keyword,
              searchedAt: new Date().toISOString(),
              type: 'MENU' as SearchType,
              franchiseId: null
            }));
            setRecentSearches(converted);
            localStorage.setItem('recentSearches', JSON.stringify(converted));
          } else {
            // 유효한 객체 배열인지 확인하고 type과 franchiseId 필드 추가 (없는 경우)
            const validSearches = parsed.filter((item: any) => 
              item && 
              typeof item === 'object' && 
              typeof item.keyword === 'string' && 
              item.keyword.trim() !== ''
            ).map((item: any) => ({
              keyword: item.keyword,
              searchedAt: item.searchedAt || new Date().toISOString(),
              type: item.type || 'MENU' as SearchType,
              franchiseId: item.franchiseId || null
            }));
            
            setRecentSearches(validSearches);
            
            // 마이그레이션된 데이터 다시 저장
            if (validSearches.length !== parsed.length || !parsed[0]?.type) {
              localStorage.setItem('recentSearches', JSON.stringify(validSearches));
            }
          }
        } else {
          setRecentSearches([]);
        }
      } catch (error) {
        console.error('Failed to load recent searches:', error);
        setRecentSearches([]);
        // 잘못된 데이터 삭제
        localStorage.removeItem('recentSearches');
      }
    };

    loadRecentSearches();
  }, []);

  // 최근 검색어 추가 함수
  const addRecentSearchLocal = (keyword: string, type: SearchType = 'MENU', franchiseId: string | null = null) => {
    try {
      const newSearch = {
        keyword: keyword.trim(),
        searchedAt: new Date().toISOString(),
        type,
        franchiseId
      };
      
      const current = recentSearches.filter(item => item.keyword !== keyword.trim());
      const updated = [newSearch, ...current].slice(0, 10);
      
      setRecentSearches(updated);
      localStorage.setItem('recentSearches', JSON.stringify(updated));
    } catch (error) {
      console.error('Failed to save recent search:', error);
    }
  };

  // 최근 검색어 개별 삭제 함수
  const removeRecentSearchLocal = (keyword: string) => {
    try {
      const filtered = recentSearches.filter(item => item.keyword !== keyword);
      setRecentSearches(filtered);
      localStorage.setItem('recentSearches', JSON.stringify(filtered));
    } catch (error) {
      console.error('Failed to remove recent search:', error);
    }
  };

  // 최근 검색어 전체 삭제 함수
  const clearRecentSearchesLocal = () => {
    try {
      setRecentSearches([]);
      localStorage.removeItem('recentSearches');
    } catch (error) {
      console.error('Failed to clear recent searches:', error);
    }
  };

  // 업데이트 시간 포맷팅
  const formatUpdateTime = (lastUpdated: string) => {
    try {
      if (!lastUpdated) return '';
      
      const date = new Date(lastUpdated);
      
      // 유효한 날짜인지 확인
      if (isNaN(date.getTime())) return '';
      
      const now = new Date();
      const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
      const diffInHours = Math.floor(diffInMinutes / 60);
      const diffInDays = Math.floor(diffInHours / 24);
      
      if (diffInMinutes < 1) {
        return texts.justNow;
      } else if (diffInMinutes < 60) {
        return `${diffInMinutes}${texts.minutesAgo}`;
      } else if (diffInHours < 24) {
        return `${diffInHours}${texts.hoursAgo}`;
      } else {
        return `${diffInDays}${texts.daysAgo}`;
      }
    } catch (error) {
      return '';
    }
  };

  // 동적 플레이스홀더 생성
  const getDynamicPlaceholder = () => {
    return `${texts.searchPlaceholder} "${foodItems[currentFoodIndex]}"`;
  };

  // 검색 실행 (SearchBar에서 직접 입력 - 항상 MENU 타입)
  const handleSearch = (keyword: string) => {
    if (keyword.trim()) {
      addRecentSearchLocal(keyword.trim(), 'MENU', null);
      const params = new URLSearchParams({
        menuName: keyword.trim(),
        searchType: 'MENU',
        lang: currentLang
      });
      navigate(`/search-results?${params.toString()}`);
    }
  };

  // 폼 제출 핸들러
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSearch(searchTerm);
  };

  // 인기 검색어 클릭
  const handlePopularSearchClick = (item: SearchRankingItem) => {
    addRecentSearchLocal(item.keyword, item.type, item.franchiseId);
    
    const params = new URLSearchParams({
      searchType: item.type,
      lang: currentLang
    });

    if (item.type === 'MENU') {
      params.append('menuName', item.keyword);
    } else if (item.type === 'FRANCHISE' && item.franchiseId) {
      params.append('franchiseId', item.franchiseId);
    }

    navigate(`/search-results?${params.toString()}`);
  };

  // 최근 검색어 클릭
  const handleRecentSearchClick = (item: RecentSearchItem) => {
    // 클릭 시 최신 검색어로 업데이트
    addRecentSearchLocal(item.keyword, item.type, item.franchiseId);
    
    const params = new URLSearchParams({
      searchType: item.type,
      lang: currentLang
    });

    if (item.type === 'MENU') {
      params.append('menuName', item.keyword);
    } else if (item.type === 'FRANCHISE' && item.franchiseId) {
      params.append('franchiseId', item.franchiseId);
    }

    navigate(`/search-results?${params.toString()}`);
  };

  // 최근 검색어 삭제
  const handleRemoveRecentSearch = (keyword: string, e: React.MouseEvent) => {
    e.stopPropagation();
    removeRecentSearchLocal(keyword);
  };

  // 최근 검색어 전체 삭제
  const handleClearAllRecentSearches = () => {
    clearRecentSearchesLocal();
  };

  // 뒤로 가기
  const handleBack = useCallback(() => {
    // 입력 필드가 포커스된 상태라면 먼저 포커스 해제
    const activeElement = document.activeElement as HTMLElement;
    if (activeElement && activeElement.tagName === 'INPUT') {
      activeElement.blur();
      // 포커스 해제 후 약간의 딜레이를 주고 뒤로 가기
      setTimeout(() => {
        navigate(-1);
      }, 100);
    } else {
      navigate(-1);
    }
  }, [navigate]);

  // 키보드 이벤트 처리 (ESC 키로 뒤로 가기)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleBack();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleBack]);

  // 브라우저 뒤로 가기 버튼 처리
  useEffect(() => {
    const handlePopState = () => {
      // 현재 페이지에서 뒤로 가기가 발생했을 때의 처리
      // 이미 navigate(-1)로 처리되므로 추가 작업 불필요
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // 상태별 아이콘 렌더링
  const renderStatusIcon = (status: string, changeAmount: number | null) => {
    switch (status) {
      case 'UP':
        return (
          <span className="text-red-500 text-xs flex items-center">
            <svg className="w-3 h-3 mr-0.5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M7 14l5-5 5 5z"/>
            </svg>
            {changeAmount}
          </span>
        );
      case 'DOWN':
        return (
          <span className="text-blue-500 text-xs flex items-center">
            <svg className="w-3 h-3 mr-0.5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M7 10l5 5 5-5z"/>
            </svg>
            {changeAmount}
          </span>
        );
      case 'NEW':
        return <span className="text-green-500 text-xs font-medium">NEW</span>;
      default:
        return <span className="text-gray-400 text-xs">-</span>;
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* 헤더 */}
      <div className="flex items-center px-4 py-3 border-b border-gray-200">
        <button
          onClick={handleBack}
          className="p-2 mr-2 rounded-full hover:bg-gray-100 active:bg-gray-200 transition-colors"
          aria-label="뒤로 가기"
        >
          <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        
        <form onSubmit={handleSubmit} className="flex-1">
          <div className="relative">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={getDynamicPlaceholder()}
              className="w-full px-4 py-2 pr-12 bg-gray-50 rounded-2xl md-body-large md-on-surface placeholder:md-on-surface-variant outline-none focus:outline-none focus:ring-2 focus:ring-[#dc2626] focus:bg-white transition-all duration-300"
            />
            
            {/* Search Icon */}
            <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="w-6 h-6 text-gray-500"
              >
                <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
              </svg>
            </div>
          </div>
        </form>        
      </div>

      <div className="px-4 py-6">
        {/* 인기 검색어 섹션 */}
        <div className="mb-8">
          <div className="flex flex-col mb-4">
            <h2 className="text-lg font-semibold text-left md-on-surface">{texts.popularSearches}</h2>
            <div className="flex items-center justify-between mt-1">
              <span className="text-sm text-gray-500">{texts.weeklyRanking}</span>
              {lastUpdated && (
                <span className="text-xs text-gray-400">
                  {texts.lastUpdated} {formatUpdateTime(lastUpdated)}
                </span>
              )}
            </div>
          </div>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#dc2626]"></div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 grid-flow-col grid-rows-5">
              {searchRanking.slice(0, 10).map((item) => (
                <div
                  key={item.rank}
                  onClick={() => handlePopularSearchClick(item)}
                  className="flex items-center justify-between p-3 rounded-lg bg-gray-50 hover:bg-gray-100 active:bg-gray-200 transition-colors cursor-pointer"
                >
                  <div className="flex items-center min-w-0 flex-1">
                    <span className="w-5 h-5 text-xs font-bold text-[#dc2626] mr-2 flex-shrink-0">
                      {item.rank}
                    </span>
                    <span className="md-body-medium md-on-surface truncate">{item.keyword}</span>
                  </div>
                  <div className="ml-2 flex-shrink-0">
                    {renderStatusIcon(item.status, item.changeAmount)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 섹션 구분선 */}
        <div className="border-t border-gray-200 mb-6"></div>

        {/* 최근 검색어 섹션 */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold md-on-surface">{texts.recentSearches}</h2>
            {recentSearches.length > 0 && (
              <button
                onClick={handleClearAllRecentSearches}
                className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                {texts.clearAll}
              </button>
            )}
          </div>
          
          {recentSearches.length === 0 ? (
            <div className="py-8 text-center">
              <span className="text-gray-500 md-body-medium">{texts.noRecentSearches}</span>
            </div>
          ) : (
            <div className="space-y-0">
              {recentSearches.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-betweenrounded-lg  active:bg-gray-100 transition-colors"
                >
                  <div 
                    className="flex items-center min-w-0 flex-1 cursor-pointer"
                    onClick={() => handleRecentSearchClick(item)}
                  >
                    <svg className="w-5 h-5 text-gray-400 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M13 3c-4.97 0-9 4.03-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42C8.27 19.99 10.51 21 13 21c4.97 0 9-4.03 9-9s-4.03-9-9-9zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12z"/>
                    </svg>
                    <span className="md-body-large md-on-surface truncate">{item.keyword}</span>
                  </div>
                  
                  <div className="flex items-center ml-3 flex-shrink-0">
                    {item.searchedAt && formatUpdateTime(item.searchedAt) && (
                      <span className="text-xs text-gray-400 mr-3">{formatUpdateTime(item.searchedAt)}</span>
                    )}
                    <button
                      onClick={(e) => handleRemoveRecentSearch(item.keyword, e)}
                      className="rounded-full hover:bg-gray-200 active:bg-gray-300 transition-colors min-h-[44px] flex items-center justify-center"
                      aria-label="삭제"
                    >
                      <svg className="w-5 h-5 text-gray-500" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchPage; 